import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyAssignments } from '../hooks/useMyAssignments';
import { useMyPerformerNotes, useMyEventNotes, useUpdateMyPerformerNotes } from '../hooks/useNotes';
import { useAllEvents } from '../hooks/useInventory';
import { formatEventDateOnly, formatDateTimeEastern } from '../utils/dates';
import { openCompare } from '../utils/compareLink';
import { isStaff } from '../utils/roles';
import { useAiAttention } from '../hooks/useAi';

// Keys must match backend/utils/dateWindows.js
const DATE_WINDOW_LABELS = { w1: '0–1 week out', w2: '1 week–1 month out', w3: '1 month+ out' };

function MyArtists({ user, onLogout }) {
  const navigate = useNavigate();
  const { data: assignments, isLoading } = useMyAssignments(true);

  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [expandedArtist, setExpandedArtist] = useState(null);
  // Staged general-notes edits keyed by LOWERCASED artist name so case-variant
  // duplicate rows collapse into one staged value; `name` keeps display casing
  // for the save payload
  const [notesChanges, setNotesChanges] = useState({});
  const [showPastEventNotes, setShowPastEventNotes] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  // General notes shared across all users (pricer-scoped server-side)
  const { data: performerNotes = {} } = useMyPerformerNotes();
  const updateNotesMutation = useUpdateMyPerformerNotes();
  // Events + event notes only load once a row is expanded - the full-inventory
  // fetch is the heaviest call this page can make
  const { data: myEventNotes = [] } = useMyEventNotes({ enabled: expandedArtist !== null });
  const { data: allEvents = [], isLoading: eventsLoading } = useAllEvents({ enabled: expandedArtist !== null });

  // Lowercase-keyed view of the notes map: assignment names can differ in
  // casing from the stored performer names. Null prototype so a performer
  // named "__proto__" can't clobber the object.
  const notesByLower = useMemo(() => {
    const map = Object.create(null);
    for (const [name, entry] of Object.entries(performerNotes)) {
      map[name.toLowerCase()] = entry;
    }
    return map;
  }, [performerNotes]);

  // Drop staged entries that now match the server value (e.g. another user
  // saved the same text) so they don't linger as phantom unsaved changes
  useEffect(() => {
    setNotesChanges(prev => {
      let changed = false;
      const next = { ...prev };
      for (const [key, entry] of Object.entries(prev)) {
        if (entry.value === (notesByLower[key]?.notes || '')) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [notesByLower]);

  const getEffectiveNotes = (artistName) => {
    const key = artistName.toLowerCase();
    if (notesChanges[key] !== undefined) return notesChanges[key].value;
    return notesByLower[key]?.notes || '';
  };

  // Stage per keystroke (textareas are controlled - artist lists are small);
  // a value matching the server is unstaged entirely so an untouched or
  // reverted edit never counts (or re-saves) as a change
  const handleNotesChange = (artistName, value) => {
    const key = artistName.toLowerCase();
    const serverValue = notesByLower[key]?.notes || '';
    setNotesChanges(prev => {
      if (value === serverValue) {
        if (prev[key] === undefined) return prev;
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: { name: artistName, value } };
    });
  };

  const messageTimer = useRef(null);
  const errorTimer = useRef(null);
  const showSaveMessage = (msg) => {
    setSaveMessage(msg);
    clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setSaveMessage(''), 3000);
  };
  const showSaveError = (msg) => {
    setSaveError(msg);
    clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setSaveError(''), 5000);
  };
  useEffect(() => () => {
    clearTimeout(messageTimer.current);
    clearTimeout(errorTimer.current);
  }, []);

  const handleSaveNotes = async () => {
    // Snapshot what this save sends so edits made while the request is in
    // flight are never cleared along with it
    const saved = { ...notesChanges };
    const entries = Object.values(saved);
    if (entries.length === 0) return;
    const payload = Object.create(null);
    for (const entry of entries) {
      payload[entry.name] = entry.value;
    }
    try {
      await updateNotesMutation.mutateAsync(payload);
      setNotesChanges(prev => {
        const next = { ...prev };
        for (const [key, entry] of Object.entries(saved)) {
          if (next[key] !== undefined && next[key].value === entry.value) {
            delete next[key];
          }
        }
        return next;
      });
      showSaveMessage(`Saved notes for ${entries.length} artist(s)`);
    } catch (err) {
      // Keep staged changes so nothing is lost on failure
      showSaveError(err.response?.data?.error || 'Failed to save notes');
    }
  };

  // Same URL Dashboard's openPricingTool builds
  // URL builder shared in utils/compareLink
  const openCompareForEvent = (event) => openCompare(event);

  // AI attention flags → "needs review" badge on expanded event cards.
  // Cheap SQLite read; returns {enabled:false} when the assistant is off.
  const { data: attentionData } = useAiAttention();
  const attentionByEvent = useMemo(() => {
    const m = new Map();
    if (attentionData?.enabled) for (const f of attentionData.flags || []) m.set(String(f.eventId), f);
    return m;
  }, [attentionData]);

  // Build flat list of all artists with their tier info
  const allArtists = useMemo(() => {
    if (!assignments) return [];

    const artists = [];

    // Add tier-based artists
    assignments.tiers?.forEach(tierGroup => {
      tierGroup.artists.forEach(artist => {
        artists.push({
          name: artist,
          tier: tierGroup.tier,
          source: 'tier'
        });
      });
    });

    // Add direct assignments
    assignments.directAssignments?.forEach(artist => {
      // Check if already added via tier
      if (!artists.some(a => a.name.toLowerCase() === artist.toLowerCase())) {
        artists.push({
          name: artist,
          tier: null,
          source: 'direct'
        });
      }
    });

    return artists.sort((a, b) => a.name.localeCompare(b.name));
  }, [assignments]);

  // Filter artists
  const filteredArtists = useMemo(() => {
    return allArtists.filter(artist => {
      const matchesSearch = !search || artist.name.toLowerCase().includes(search.toLowerCase());
      const matchesTier = tierFilter === 'all' ||
        (tierFilter === 'direct' && artist.source === 'direct') ||
        artist.tier === tierFilter;
      return matchesSearch && matchesTier;
    });
  }, [allArtists, search, tierFilter]);

  // Calculate tier stats
  const tierStats = useMemo(() => {
    const stats = {
      'S': 0, '1': 0, '2': 0, '3': 0,
      'Broadway': 0, 'Presales': 0, 'Parking': 0,
      'direct': 0
    };

    allArtists.forEach(artist => {
      if (artist.source === 'direct' && !artist.tier) {
        stats['direct']++;
      } else if (artist.tier) {
        stats[artist.tier] = (stats[artist.tier] || 0) + 1;
      }
    });

    return stats;
  }, [allArtists]);

  // Get assigned tier keys for filtering
  const assignedTiers = useMemo(() => {
    return assignments?.tiers?.map(t => t.tier) || [];
  }, [assignments]);

  const getTierLabel = (tier) => {
    if (tier === 'S') return 'Tier S';
    if (['Broadway', 'Presales', 'Parking'].includes(tier)) return tier;
    return `Tier ${tier}`;
  };

  return (
    <div className="app-container">
      <nav className="navbar">
        <div className="navbar-brand">Pricing Portal</div>
        <div className="navbar-user">
          <span>{user.displayName} ({user.role})</span>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/')}>
            Dashboard
          </button>
          {isStaff(user.role) && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/orders')}>
              Note Requests
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={onLogout}>
            Logout
          </button>
        </div>
      </nav>

      <main className="main-content">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">My Assigned Artists</h2>
            <span style={{ color: '#a0a0a0', fontSize: '0.85rem' }}>
              {assignments?.totalArtistCount || 0} total artists
            </span>
          </div>

          {/* Date window info */}
          {assignments?.dateAssignment?.isActive && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              background: '#2d2a1f',
              borderRadius: '6px',
              border: '1px solid #4d4a3f',
              fontSize: '0.85rem'
            }}>
              <strong>Date Window Active:</strong>{' '}
              {assignments.dateAssignment.mode === 'rolling'
                ? (assignments.dateAssignment.windows || [])
                    .map(k => DATE_WINDOW_LABELS[k])
                    .filter(Boolean).join(', ')
                : `${assignments.dateAssignment.startDate} to ${assignments.dateAssignment.endDate}`
              }
              <span style={{ color: '#a0a0a0', marginLeft: '0.5rem' }}>
                (shows events within this window regardless of artist)
              </span>
            </div>
          )}

          {/* Tier summary badges */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {[
              { key: 'S', label: 'S', cls: 'tier-s' },
              { key: '1', label: 'Tier 1', cls: 'tier-1' },
              { key: '2', label: 'Tier 2', cls: 'tier-2' },
              { key: '3', label: 'Tier 3', cls: 'tier-3' },
              { key: 'Broadway', label: 'Broadway', cls: 'tier-broadway' },
              { key: 'Presales', label: 'Presales', cls: 'tier-presales' },
              { key: 'Parking', label: 'Parking', cls: 'tier-parking' },
            ].filter(t => assignedTiers.includes(t.key)).map(t => (
              <div
                key={t.key}
                className={`tier-badge ${t.cls}`}
                style={{
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  opacity: tierFilter === t.key || tierFilter === 'all' ? 1 : 0.5
                }}
                onClick={() => setTierFilter(tierFilter === t.key ? 'all' : t.key)}
              >
                {t.label}: {tierStats[t.key] || 0}
              </div>
            ))}
            {assignments?.directAssignments?.length > 0 && (
              <div
                className="tier-badge"
                style={{
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  background: '#2a3f5f',
                  opacity: tierFilter === 'direct' || tierFilter === 'all' ? 1 : 0.5
                }}
                onClick={() => setTierFilter(tierFilter === 'direct' ? 'all' : 'direct')}
              >
                Direct: {tierStats['direct'] || 0}
              </div>
            )}
          </div>

          {/* Search */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: '200px' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search artists..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <select
                className="form-input"
                value={tierFilter}
                onChange={e => setTierFilter(e.target.value)}
              >
                <option value="all">All Tiers</option>
                {assignedTiers.map(tier => (
                  <option key={tier} value={tier}>{getTierLabel(tier)}</option>
                ))}
                {assignments?.directAssignments?.length > 0 && (
                  <option value="direct">Direct Assignments</option>
                )}
              </select>
            </div>
          </div>

          {/* Save bar for staged general-notes edits */}
          {saveMessage && <div className="message message-success">{saveMessage}</div>}
          {saveError && <div className="message message-error">{saveError}</div>}
          {Object.keys(notesChanges).length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              marginBottom: '0.75rem', padding: '0.6rem 1rem',
              background: '#1a1a2e', borderRadius: '6px', border: '1px solid #3d3d5c'
            }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                {Object.keys(notesChanges).length} unsaved note change(s)
              </span>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSaveNotes}
                disabled={updateNotesMutation.isPending}
              >
                {updateNotesMutation.isPending ? 'Saving...' : `Save Notes (${Object.keys(notesChanges).length})`}
              </button>
            </div>
          )}

          {isLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#a0a0a0' }}>
              Loading assignments...
            </div>
          ) : (
            <>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Artist</th>
                      <th>Tier</th>
                      <th>General Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredArtists.length === 0 ? (
                      <tr>
                        <td colSpan="3" style={{ textAlign: 'center', color: '#a0a0a0' }}>
                          {allArtists.length === 0
                            ? 'No artists assigned. Contact an admin.'
                            : 'No artists match your search'
                          }
                        </td>
                      </tr>
                    ) : (
                      filteredArtists.map((artist, idx) => {
                        const lowerName = artist.name.toLowerCase();
                        const isStaged = notesChanges[lowerName] !== undefined;
                        const isExpanded = expandedArtist === artist.name;
                        const noteEntry = notesByLower[lowerName];

                        let performerEvents = [];
                        let pastNotes = [];
                        if (isExpanded) {
                          const noteByEventId = new Map(myEventNotes.map(n => [String(n.eventId), n]));
                          // Exact lowercase match (substring matching would list
                          // "Museum Tour" under "Muse"); allEvents is already
                          // pricer-scoped server-side and sorted soonest-first
                          performerEvents = allEvents
                            .filter(e => (e.performer || '').toLowerCase() === lowerName)
                            .map(e => ({ ...e, note: noteByEventId.get(String(e.eventId)) }));
                          const liveIds = new Set(performerEvents.map(e => String(e.eventId)));
                          pastNotes = myEventNotes.filter(n =>
                            (n.performerName || '').toLowerCase() === lowerName && !liveIds.has(String(n.eventId))
                          );
                        }

                        return (
                          <React.Fragment key={`${artist.name}-${idx}`}>
                          <tr style={isStaged ? { background: '#1a2a1a' } : {}}>
                            <td
                              style={{ fontWeight: 'bold', cursor: 'pointer' }}
                              onClick={() => setExpandedArtist(isExpanded ? null : artist.name)}
                              title="Show this artist's events"
                            >
                              {artist.name}{' '}
                              <span style={{ color: '#a0a0a0', fontSize: '0.7rem', fontWeight: 'normal' }}>
                                {isExpanded ? '▲' : '▼'}
                              </span>
                            </td>
                            <td>
                              {artist.tier ? (
                                <span className={`tier-badge tier-${artist.tier.toLowerCase()}`}>
                                  {artist.tier === 'S' ? 'Tier S' :
                                   ['Broadway', 'Presales', 'Parking'].includes(artist.tier) ? artist.tier :
                                   `Tier ${artist.tier}`}
                                </span>
                              ) : (
                                <span className="tier-badge" style={{ background: '#2a3f5f' }}>
                                  Direct
                                </span>
                              )}
                            </td>
                            <td>
                              <textarea
                                className="form-input"
                                rows={2}
                                value={getEffectiveNotes(artist.name)}
                                onChange={e => handleNotesChange(artist.name, e.target.value)}
                                placeholder="General notes..."
                                maxLength={5000}
                                style={{ width: '260px', fontSize: '0.8rem', resize: 'vertical', padding: '0.25rem 0.5rem' }}
                              />
                              {noteEntry?.updatedBy && (
                                <div style={{ fontSize: '0.65rem', color: '#a0a0a0' }}>
                                  Last updated by {noteEntry.updatedBy}
                                  {noteEntry.updatedAt ? ` · ${formatDateTimeEastern(noteEntry.updatedAt)}` : ''}
                                </div>
                              )}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="3" style={{ background: '#1a1a2e', padding: '1rem' }}>
                                {eventsLoading ? (
                                  <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>Loading events...</div>
                                ) : (
                                  <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                      <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                                        Events ({performerEvents.length})
                                      </span>
                                      {pastNotes.length > 0 && (
                                        <button
                                          className="btn btn-secondary btn-sm"
                                          style={{ padding: '0.15rem 0.4rem', fontSize: '0.7rem' }}
                                          onClick={() => setShowPastEventNotes(!showPastEventNotes)}
                                        >
                                          {showPastEventNotes ? 'Hide' : 'Show'} past events with notes ({pastNotes.length})
                                        </button>
                                      )}
                                    </div>
                                    {performerEvents.length === 0 && pastNotes.length === 0 && (
                                      <div style={{ color: '#a0a0a0', fontSize: '0.75rem' }}>
                                        No events in inventory for this artist
                                      </div>
                                    )}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.5rem' }}>
                                      {performerEvents.map(event => (
                                        <div
                                          key={event.eventId}
                                          style={{ background: '#252540', padding: '0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}
                                        >
                                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'flex-start' }}>
                                            <div style={{ fontWeight: 'bold' }}>
                                              {event.eventName}
                                              {attentionByEvent.has(String(event.eventId)) && (
                                                <span className="ai-flag-badge" title={(attentionByEvent.get(String(event.eventId)).reasons || []).join(' · ')}>
                                                  needs review
                                                </span>
                                              )}
                                            </div>
                                            <button
                                              className="btn btn-secondary btn-sm"
                                              style={{ padding: '0.15rem 0.4rem', fontSize: '0.7rem', flexShrink: 0 }}
                                              onClick={() => openCompareForEvent(event)}
                                              title="Open this event's Compare page"
                                            >
                                              Open
                                            </button>
                                          </div>
                                          <div style={{ color: '#a0a0a0' }}>
                                            {formatEventDateOnly(event.eventDate, { weekday: 'short' })} • {event.venue}
                                          </div>
                                          <div style={{ marginTop: '0.35rem', whiteSpace: 'pre-wrap' }}>
                                            {event.note?.notes
                                              ? event.note.notes
                                              : <span style={{ color: '#a0a0a0', fontStyle: 'italic' }}>No notes</span>}
                                          </div>
                                          {event.note?.updatedBy && (
                                            <div style={{ color: '#a0a0a0', fontSize: '0.65rem', marginTop: '0.25rem' }}>
                                              Last updated by {event.note.updatedBy}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                      {showPastEventNotes && pastNotes.map(note => (
                                        <div
                                          key={`past-${note.eventId}`}
                                          style={{ background: '#252540', padding: '0.5rem', borderRadius: '4px', fontSize: '0.75rem', opacity: 0.75 }}
                                        >
                                          <div style={{ fontWeight: 'bold' }}>
                                            {note.eventName || `Event ${note.eventId}`}{' '}
                                            <span style={{ color: '#e8a09a', fontWeight: 'normal', fontSize: '0.65rem' }}>
                                              (no longer in inventory)
                                            </span>
                                          </div>
                                          <div style={{ color: '#a0a0a0' }}>
                                            {note.eventDate ? formatEventDateOnly(note.eventDate, { weekday: 'short' }) : 'Unknown date'}
                                            {note.venue ? ` • ${note.venue}` : ''}
                                          </div>
                                          <div style={{ marginTop: '0.35rem', whiteSpace: 'pre-wrap' }}>{note.notes}</div>
                                          {note.updatedBy && (
                                            <div style={{ color: '#a0a0a0', fontSize: '0.65rem', marginTop: '0.25rem' }}>
                                              Last updated by {note.updatedBy}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: '0.75rem', color: '#a0a0a0', fontSize: '0.8rem' }}>
                Showing {filteredArtists.length} of {allArtists.length} artists
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default MyArtists;
